import React from "react";
import {Skeleton} from "@mui/material";

/**
 * Facebook-style shimmer skeleton loaders (MUI Skeleton animation="wave").
 * Used in content areas while a page/section's data is still loading, in place
 * of a bare spinner. Each variant mimics the shape of the content it replaces.
 */

// A rounded "wave" bar. Width/height are passed through to MUI Skeleton.
function Bar({width, height = 16, className}) {
    return (
        <Skeleton animation="wave" variant="rounded" width={width} height={height}
                  className={className} sx={{borderRadius: '0.5rem'}}/>
    );
}

/**
 * Month-grouped history pages (tip/buy/dance/drink): a couple of ring cards,
 * each with a gray header row and a few table-ish lines.
 */
export function HistorySkeleton({cards = 2, rows = 3}) {
    return (
        <div className="space-y-6" aria-busy="true">
            {Array.from({length: cards}).map((_, i) => (
                <div key={i} className="overflow-hidden rounded-xl ring-1 ring-gray-200 dark:ring-gray-700">
                    <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 px-5 py-4">
                        <Bar width={120} height={18}/>
                        <Bar width={80} height={18}/>
                    </div>
                    <div className="divide-y divide-gray-100 dark:divide-gray-700 px-5">
                        {Array.from({length: rows}).map((__, j) => (
                            <div key={j} className="flex items-center justify-between py-3">
                                <Bar width={140} height={14}/>
                                <Bar width={60} height={14}/>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

/**
 * Package tier grid (buy/sell VT, dance, drink): a row of package cards.
 */
export function TierGridSkeleton({count = 4}) {
    return (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-busy="true">
            {Array.from({length: count}).map((_, i) => (
                <div key={i} className="rounded-2xl ring-1 ring-gray-200 dark:ring-gray-700 p-6 space-y-3">
                    <Bar width={100} height={18}/>
                    <Bar width={140} height={32}/>
                    <Bar width="100%" height={14}/>
                    <Bar width="60%" height={14}/>
                </div>
            ))}
        </div>
    );
}

/**
 * A simple vertical list of rows (checked-in dancers, selection lists).
 */
export function DancerListSkeleton({rows = 4}) {
    return (
        <ul className="divide-y divide-gray-200 dark:divide-gray-700" aria-busy="true">
            {Array.from({length: rows}).map((_, i) => (
                <li key={i} className="py-4">
                    <div className="flex items-center space-x-4">
                        <Skeleton animation="wave" variant="circular" width={32} height={32}/>
                        <div className="min-w-0 flex-1 space-y-2">
                            <Bar width={120} height={14}/>
                            <Bar width={80} height={12}/>
                        </div>
                        <Bar width={64} height={28}/>
                    </div>
                </li>
            ))}
        </ul>
    );
}

/**
 * An editable section list (dashboard-edit tiers/packages/rewards/accounts).
 */
export function SectionListSkeleton({rows = 3}) {
    return (
        <ul className="mt-2 divide-y divide-gray-100 dark:divide-gray-700 border-t border-gray-200 dark:border-gray-700" aria-busy="true">
            {Array.from({length: rows}).map((_, i) => (
                <li key={i} className="flex justify-between gap-x-6 py-6">
                    <Bar width={220} height={16}/>
                    <Bar width={60} height={16}/>
                </li>
            ))}
        </ul>
    );
}

/**
 * A grid of cards (invoices).
 */
export function CardGridSkeleton({count = 3}) {
    return (
        <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-8 lg:grid-cols-3 xl:gap-x-8" aria-busy="true">
            {Array.from({length: count}).map((_, i) => (
                <li key={i} className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className="divide-y divide-gray-100 dark:divide-gray-700 px-6 py-4 space-y-3">
                        <div className="flex justify-between pt-3"><Bar width={70} height={14}/><Bar width={100} height={14}/></div>
                        <div className="flex justify-between pt-3"><Bar width={90} height={14}/><Bar width={80} height={14}/></div>
                        <div className="flex justify-between pt-3"><Bar width={110} height={14}/><Bar width={90} height={28}/></div>
                    </div>
                </li>
            ))}
        </ul>
    );
}

/**
 * A centered stat block (dancer current money / money owed, VT balance, rewards).
 */
export function StatBlockSkeleton() {
    return (
        <div className="mx-auto max-w-2xl px-6 py-10 space-y-6" aria-busy="true">
            <div className="flex flex-col items-center space-y-3">
                <Bar width={160} height={20}/>
                <Bar width={200} height={40}/>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {Array.from({length: 3}).map((_, i) => (
                    <div key={i} className="rounded-2xl ring-1 ring-gray-200 dark:ring-gray-700 p-5 space-y-2">
                        <Bar width={80} height={14}/>
                        <Bar width={110} height={28}/>
                    </div>
                ))}
            </div>
        </div>
    );
}

/**
 * A single large rounded square (QR code placeholder).
 */
export function SquareSkeleton({size = 280}) {
    return (
        <div className="mx-auto mt-6 flex items-center justify-center" style={{maxWidth: size}}>
            <Skeleton animation="wave" variant="rounded" width={size} height={size}
                      sx={{borderRadius: '0.75rem'}}/>
        </div>
    );
}

/**
 * A table-body skeleton for dashboard money/history sections (rows inside a <tbody>).
 * Renders <tr>/<td> so it is valid inside an existing table.
 */
export function TableRowsSkeleton({rows = 3, cols = 2}) {
    return (
        <>
            {Array.from({length: rows}).map((_, i) => (
                <tr key={i}>
                    {Array.from({length: cols}).map((__, j) => (
                        <td key={j} className={j === 0 ? "py-4 pr-6" : "py-4 text-right"}>
                            <div className={j === 0 ? "" : "flex justify-end"}>
                                <Bar width={j === 0 ? 160 : 80} height={14}/>
                            </div>
                        </td>
                    ))}
                </tr>
            ))}
        </>
    );
}
